"use client";

import { useState, useEffect, useCallback } from "react";
import { Button } from "../../button.js";
import { Card, CardContent, CardHeader, CardTitle } from "../../card.js";
import { Alert, AlertDescription } from "../../alert.js";
import { Calendar, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { generateGoogleAuthUrl } from "../../../lib/oauth-utils.js";
import { useToast } from "../../../hooks/use-toast.js";

interface GoogleOAuthSetupProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  redirectUri?: string;
}

export const GoogleOAuthSetup = ({
  onSuccess,
  onError,
  redirectUri,
}: GoogleOAuthSetupProps) => {
  const [authUrl, setAuthUrl] = useState<string>("");
  const [secondAccountAuthUrl, setSecondAccountAuthUrl] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const clientId = process.env.NEXT_PUBLIC_UNIFIEDTIME_GOOGLE_OAUTH_CLIENT_ID || "";
  const defaultRedirectUri = typeof window !== "undefined" ? `${window.location.origin}/unifiedtime/intermediate/` : "";
  const finalRedirectUri = redirectUri || defaultRedirectUri;

  const handleError = useCallback((errorMessage: string) => {
    setError(errorMessage);
    onError?.(errorMessage);
    toast({
      title: "Error",
      description: errorMessage,
      variant: "destructive",
    });
  }, [onError, toast]);

  useEffect(() => {
    const generateAuthUrls = async () => {
      try {
        setIsLoading(true);

        // Generate primary account auth URL first
        const primaryAuthUrl = await generateGoogleAuthUrl(
          false,
          clientId,
          finalRedirectUri,
        );
        setAuthUrl(primaryAuthUrl);

        // Generate secondary account auth URL second (this will store a different code_verifier)
        const secondaryAuthUrl = await generateGoogleAuthUrl(
          true,
          clientId,
          finalRedirectUri,
        );
        setSecondAccountAuthUrl(secondaryAuthUrl);
      } catch (err) {
        const errorMessage = "Failed to generate authentication URLs";
        handleError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    generateAuthUrls();
  }, [clientId, finalRedirectUri, handleError]);

  const handlePrimaryAuth = () => {
    if (authUrl && typeof window !== "undefined") {
      window.location.href = authUrl;
    }
  };

  const handleSecondaryAuth = () => {
    if (secondAccountAuthUrl && typeof window !== "undefined") {
      window.location.href = secondAccountAuthUrl;
    }
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-6">
          <div className="flex items-center space-x-2">
            <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
            <span>Preparing authentication...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center space-x-2">
            <Calendar className="h-6 w-6 text-blue-600" />
            <CardTitle>Google Calendar Setup</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-muted-foreground">
            Connect your Google Calendar to view and manage your events in
            UnifiedTime.
          </p>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <Button
              onClick={handlePrimaryAuth}
              disabled={!authUrl}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white border-2 border-blue-600 hover:border-blue-700 font-semibold py-3 text-base"
            >
              <CheckCircle className="mr-2 h-5 w-5" />
              Connect Primary Google Account
            </Button>

            <Button
              onClick={handleSecondaryAuth}
              disabled={!secondAccountAuthUrl}
              variant="outline"
              className="w-full border-2 border-green-600 text-green-700 hover:bg-green-50 hover:border-green-700 font-semibold py-3 text-base"
            >
              <Calendar className="mr-2 h-5 w-5" />
              Connect Secondary Google Account
            </Button>
          </div>

          <div className="text-sm text-muted-foreground space-y-1">
            <p>• Primary account will be used for main calendar operations</p>
            <p>• Secondary account can be used for additional calendars</p>
            <p>• You can connect both accounts to manage multiple calendars</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
